import type { WorksheetStatusHistoryEntryDto } from '@kgc/service-worksheet/worksheet-dto';
import { WorksheetStatusHistoryResponseSchema } from '@kgc/service-worksheet/worksheet-dto';
/**
 * Worksheets API Client
 * Epic 17: Munkalap CRUD
 */

import { ApiError, api } from './client';

// ============================================
// ERROR CODES
// ============================================

/**
 * WORKSHEET_PENDING_PARTS — a BE 422-t ad, ha a munkalap befejezése (→KESZ, #646 auto-consume)
 * vagy lezárása (→LEZART) függő alkatrész-beszerzéssel próbálkozik (PV-SZERVIZ-002 TILTÁS).
 * A FE a hard-gate-et disabled-gombbal jelzi (UX); ez a guard a defense-in-depth (BE-422 → toast).
 */
export const WORKSHEET_PENDING_PARTS = 'WORKSHEET_PENDING_PARTS';

export function isWorksheetPendingPartsError(err: unknown): boolean {
  if (err instanceof ApiError) return err.code === WORKSHEET_PENDING_PARTS;
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { code?: unknown }).code === WORKSHEET_PENDING_PARTS
  );
}

// ============================================
// TYPES (matching backend)
// ============================================

export enum WorksheetStatus {
  FELVEVE = 'FELVEVE',
  FOLYAMATBAN = 'FOLYAMATBAN',
  VARHATO = 'VARHATO',
  FELFUGGESZTVE = 'FELFUGGESZTVE',
  KESZ = 'KESZ',
  SZAMLAZANDO = 'SZAMLAZANDO',
  LEZART = 'LEZART',
  TOROLVE = 'TOROLVE',
}

export enum WorksheetType {
  FIZETOS = 'FIZETOS',
  GARANCIALIS = 'GARANCIALIS',
  BERLESI = 'BERLESI',
  KARBANTARTAS = 'KARBANTARTAS',
}

export enum WorksheetPriority {
  NORMAL = 'NORMAL',
  SURGOS = 'SURGOS',
  FELARAS = 'FELARAS',
  GARANCIALIS = 'GARANCIALIS',
  FRANCHISE = 'FRANCHISE',
}

export interface Worksheet {
  id: string;
  tenantId: string;
  worksheetNumber: string;
  type: WorksheetType;
  status: WorksheetStatus;
  priority: WorksheetPriority;
  partnerId: string;
  /**
   * QA-104: a partner megjelenitheto neve, ha a BE adja.
   *
   * SZANDEKOSAN OPCIONALIS (Botka-3 optional-guard): a lista a nevet hasznalja,
   * ha megerkezik, kulonben a korabbi, csonkolt azonositot rajzolja tovabb. Igy
   * ez a FE-valtozas merge-order-fuggetlen — a BE-oldali mapper-fix (a JOIN mar
   * fut, a nevet a domain-mapper dobja el) barmikor landolhat utana, es a nev
   * FE-valtozas nelkul jelenik meg.
   */
  partnerName?: string;
  deviceName: string;
  deviceSerialNumber?: string;
  faultDescription: string;
  diagnosis?: string;
  workPerformed?: string;
  internalNote?: string;
  assignedToId?: string;
  costLimit?: number;
  estimatedCompletionDate?: string;
  receivedAt: string;
  completedAt?: string;
  rentalId?: string;
  /** page10-B (#435): közös beadás-azonosító — al-szám ML-…-A/B/C a batch-en belül */
  intakeBatchId?: string;
  queuePosition?: number;
  storageStartDate?: string;
  storageDailyFee?: number;
  storageTotalFee?: number;
  inspectionFee?: number | null;
  inspectionFeePaid?: boolean;
  isWarranty?: boolean;
  /**
   * 34-kör R-1: a kiállított számla ID-je (read-DTO contract-append, Zeno PR-A).
   * A wire-státusz EBBŐL derived: COMPLETED+invoiceId=NULL → KESZ (számlázható),
   * COMPLETED+invoiceId=SET → SZAMLAZANDO (számlázva). Optional/backward-compat
   * (mint a #687 deposits[].id) — a Prisma-modellben létezik, a wire-DTO most kapja meg.
   */
  invoiceId?: string | undefined;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorksheetListResponse {
  data: Worksheet[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface WorksheetResponse {
  data: Worksheet;
}

export interface WorksheetStats {
  total: number;
  felveve: number;
  folyamatban: number;
  varhato: number;
  felfuggesztve: number;
  kesz: number;
  szamlazando: number;
  lezart: number;
}

export interface WorksheetFilters {
  status?: WorksheetStatus;
  type?: WorksheetType;
  partnerId?: string;
  assignedToId?: string;
  machineCategoryId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  /** page10-B (#435): batch-intake szűrő */
  intakeBatchId?: string;
  page?: number;
  pageSize?: number;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get list of worksheets with filters
 */
export async function getWorksheets(
  filters: WorksheetFilters = {}
): Promise<WorksheetListResponse> {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.type) params.append('type', filters.type);
  if (filters.partnerId) params.append('partnerId', filters.partnerId);
  if (filters.assignedToId) params.append('assignedToId', filters.assignedToId);
  if (filters.machineCategoryId) params.append('machineCategoryId', filters.machineCategoryId);
  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.append('dateTo', filters.dateTo);
  if (filters.search) params.append('search', filters.search);
  if (filters.intakeBatchId) params.append('intakeBatchId', filters.intakeBatchId);
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/worksheets?${queryString}` : '/worksheets';

  return api.get<WorksheetListResponse>(endpoint);
}

/**
 * Get single worksheet by ID
 */
export async function getWorksheetById(id: string): Promise<WorksheetResponse> {
  return api.get<WorksheetResponse>(`/worksheets/${id}`);
}

/**
 * page11 (#433/#438) — státusz-történet a pinnelt kontrakton (runtime Zod-parse,
 * #423 partners-minta). A wire: `{ data: [...] }`, changedAt desc, domain-státuszok.
 */
export type { WorksheetStatusHistoryEntryDto };

export async function getWorksheetStatusHistory(
  worksheetId: string
): Promise<WorksheetStatusHistoryEntryDto[]> {
  const raw = await api.get<unknown>(`/worksheets/${worksheetId}/status-history`);
  return WorksheetStatusHistoryResponseSchema.parse(raw).data;
}

/**
 * Get worksheet statistics
 */
export async function getWorksheetStats(): Promise<{ data: WorksheetStats }> {
  return api.get<{ data: WorksheetStats }>('/worksheets/stats');
}

/**
 * Create worksheet
 */
export async function createWorksheet(data: {
  partnerId: string;
  deviceName: string;
  deviceSerialNumber?: string;
  faultDescription: string;
  type?: WorksheetType;
  priority?: WorksheetPriority;
  costLimit?: number;
  estimatedCompletionDate?: string;
  internalNote?: string;
  /** Story 70-1E-a: forwarded to backend FR70 audit pipeline. */
  overrideExpiredId?: boolean;
  overrideReason?: string;
}): Promise<WorksheetResponse> {
  return api.post<WorksheetResponse>('/worksheets', data);
}

// ============================================
// page10 batch-create (#435 / Q-T040): POST /worksheets/batch
// Egy beadásból (partnerId) 1–26 munkalap egy közös `intakeBatchId` alatt,
// al-számokkal (PREFIX-YYYY-NNNN-A/-B/-C). Kontraktus: CreateWorksheetBatchSchema.
// ============================================

export interface CreateWorksheetBatchItem {
  deviceName: string;
  faultDescription: string;
  deviceSerialNumber?: string;
  costLimit?: number;
  internalNote?: string;
}

export interface CreateWorksheetBatchInput {
  partnerId: string;
  type?: WorksheetType;
  priority?: WorksheetPriority;
  /** 1–26 gép (A–Z al-szám) */
  items: CreateWorksheetBatchItem[];
}

export interface WorksheetBatchResponse {
  data: {
    intakeBatchId: string;
    worksheets: Worksheet[];
  };
}

/**
 * page10 batch-create: egy beadásból N munkalap egy közös intakeBatchId alatt.
 * Hibák: 400 (üres / >26 / batch-en belüli dup-serial), 409 (gép már aktív munkalapon).
 */
export async function createWorksheetBatch(
  data: CreateWorksheetBatchInput
): Promise<WorksheetBatchResponse> {
  return api.post<WorksheetBatchResponse>('/worksheets/batch', data);
}

/**
 * Update worksheet
 */
export async function updateWorksheet(
  id: string,
  data: {
    deviceName?: string;
    deviceSerialNumber?: string;
    faultDescription?: string;
    diagnosis?: string;
    workPerformed?: string;
    priority?: WorksheetPriority;
    costLimit?: number;
    estimatedCompletionDate?: string;
    internalNote?: string;
  }
): Promise<WorksheetResponse> {
  return api.patch<WorksheetResponse>(`/worksheets/${id}`, data);
}

/**
 * Change worksheet status
 */
export async function changeWorksheetStatus(
  id: string,
  status: WorksheetStatus,
  reason?: string
): Promise<WorksheetResponse> {
  return api.patch<WorksheetResponse>(`/worksheets/${id}/status`, { status, reason });
}

// ============================================
// STATUS LABELS
// ============================================

export const STATUS_LABEL_KEYS: Record<WorksheetStatus, string> = {
  [WorksheetStatus.FELVEVE]: 'worksheet.status.felveve',
  [WorksheetStatus.FOLYAMATBAN]: 'worksheet.status.folyamatban',
  [WorksheetStatus.VARHATO]: 'worksheet.status.varhato',
  [WorksheetStatus.FELFUGGESZTVE]: 'worksheet.status.felfuggesztve',
  [WorksheetStatus.KESZ]: 'worksheet.status.kesz',
  [WorksheetStatus.SZAMLAZANDO]: 'worksheet.status.szamlazando',
  [WorksheetStatus.LEZART]: 'worksheet.status.lezart',
  [WorksheetStatus.TOROLVE]: 'worksheet.status.torolve',
};

export const TYPE_LABEL_KEYS: Record<WorksheetType, string> = {
  [WorksheetType.FIZETOS]: 'worksheet.type.fizetos',
  [WorksheetType.GARANCIALIS]: 'worksheet.type.garancialis',
  [WorksheetType.BERLESI]: 'worksheet.type.berlesi',
  [WorksheetType.KARBANTARTAS]: 'worksheet.type.karbantartas',
};

export const PRIORITY_LABEL_KEYS: Record<WorksheetPriority, string> = {
  [WorksheetPriority.SURGOS]: 'worksheet.priority.surgos',
  [WorksheetPriority.FELARAS]: 'worksheet.priority.felaras',
  [WorksheetPriority.GARANCIALIS]: 'worksheet.priority.garancialis',
  [WorksheetPriority.FRANCHISE]: 'worksheet.priority.franchise',
  [WorksheetPriority.NORMAL]: 'worksheet.priority.normal',
};

// ============================================
// SRV-F1-2: INSPECTION FEE CREDIT API
// ============================================

export interface InspectionFeeCreditResult {
  creditAmount: number;
  hasCredit: boolean;
  description: string;
  reason: string;
}

/**
 * Get inspection fee credit calculation for a worksheet.
 * Used to show the credit line item on the invoice.
 */
export async function getInspectionFeeCredit(
  worksheetId: string
): Promise<{ data: InspectionFeeCreditResult }> {
  return api.get<{ data: InspectionFeeCreditResult }>(
    `/worksheets/${worksheetId}/inspection-fee-credit`
  );
}

export interface WorksheetSummary {
  partsNetAmount: number;
  laborNetAmount: number;
  otherNetAmount: number;
  externalCostNetAmount: number;
  totalNetAmount: number;
  totalGrossAmount: number;
  vatAmount: number;
  inspectionFeeCredit?: {
    amount: number;
    description: string;
  } | null;
}

/**
 * Get financial summary with inspection fee credit for a worksheet.
 */
export async function getWorksheetSummary(
  worksheetId: string
): Promise<{ data: WorksheetSummary }> {
  return api.get<{ data: WorksheetSummary }>(`/worksheets/${worksheetId}/summary`);
}

// ============================================
// SRV-F1-1: PAYMENT GATE API
// ============================================

/**
 * Mark inspection fee as paid for a worksheet.
 * Enables FELVEVE → FOLYAMATBAN transition for FIZETOS types.
 */
export async function markInspectionFeePaid(worksheetId: string): Promise<WorksheetResponse> {
  return api.patch<WorksheetResponse>(`/worksheets/${worksheetId}`, {
    inspectionFeePaid: true,
  });
}

// ============================================
// 34-kör R-1: WORKSHEET BILLING (Szerviz zár→számláz)
// ============================================

/**
 * `POST /worksheets/:id/bill` — a KESZ-munkalap számlázása (Zeno-pin contract).
 *
 * A FE CSAK a `paymentMethod`-ot küldi (default CASH); a money-truth
 * (totalAmount/vatAmount) + a generált számla (invoiceId/invoiceNumber) a
 * BE-válaszból jön. Billing után a wire-státusz KESZ→SZAMLAZANDO derived-en vált
 * (mert `worksheet.invoiceId` beáll → refetch).
 *
 * Hibák (error-code → i18n-toast, `worksheetBillErrorToastKey`):
 *   409 WORKSHEET_ALREADY_INVOICED · 422 WORKSHEET_NOT_BILLABLE · 400 standard.
 */
export interface BillWorksheetDto {
  paymentMethod?: 'CASH' | 'TRANSFER';
}

export interface BillWorksheetResult {
  invoiceId: string;
  invoiceNumber: string;
  totalAmount: number;
  vatAmount: number;
}

export async function billWorksheet(
  id: string,
  dto: BillWorksheetDto
): Promise<{ data: BillWorksheetResult }> {
  return api.post<{ data: BillWorksheetResult }>(`/worksheets/${id}/bill`, dto);
}

/**
 * Billing-hiba → i18n toast-kulcs (a deposits `depositActionErrorToastKey`-mintára).
 * Defenzív kétlépcsős feloldás: előbb a strukturált `error.code` (Zeno PR-A küldi),
 * különben HTTP-status-fallback.
 */
export function worksheetBillErrorToastKey(error: unknown): string {
  if (error instanceof ApiError) {
    // (1) strukturált error.code
    switch (error.code) {
      case 'WORKSHEET_ALREADY_INVOICED':
        return 'worksheet.billError.alreadyInvoiced';
      case 'WORKSHEET_NOT_BILLABLE':
        return 'worksheet.billError.notBillable';
      case 'WARRANTY_NOT_BILLABLE':
        return 'worksheet.billError.warrantyNotBillable';
      default:
        break;
    }
    // (2) HTTP-status-fallback
    switch (error.statusCode) {
      case 409:
        return 'worksheet.billError.alreadyInvoiced';
      case 422:
        return 'worksheet.billError.notBillable';
      default:
        return 'worksheet.billError.failed';
    }
  }
  // (3) nem-ApiError (hálózat, stb.)
  return 'worksheet.billError.failed';
}

// ============================================
// WORKSHEET ITEMS API (N-SRV-3)
// ============================================

export type WorksheetItemType = 'PART' | 'LABOR' | 'EXTERNAL_COST' | 'OTHER';

export interface WorksheetItem {
  id: string;
  worksheetId: string;
  itemType: WorksheetItemType;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  productId?: string;
}

export async function getWorksheetItems(worksheetId: string): Promise<{ data: WorksheetItem[] }> {
  return api.get<{ data: WorksheetItem[] }>(`/worksheets/${worksheetId}/items`);
}

export async function addWorksheetItem(
  worksheetId: string,
  item: {
    itemType: string;
    description: string;
    quantity: number;
    unitPrice: number;
    productId?: string;
  }
): Promise<{ data: WorksheetItem }> {
  return api.post<{ data: WorksheetItem }>(`/worksheets/${worksheetId}/items`, item);
}

// ============================================
// SRV-FG-3: WORKSHEET PHOTOS API
// ============================================

export type WorksheetPhotoType = 'BEFORE' | 'AFTER' | 'DAMAGE' | 'GENERAL';

export interface WorksheetPhoto {
  id: string;
  worksheetId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  storagePath: string;
  caption?: string | null;
  photoType: WorksheetPhotoType;
  uploadedBy: string;
  uploadedAt: string;
  url: string;
}

export async function getWorksheetPhotos(worksheetId: string): Promise<{ data: WorksheetPhoto[] }> {
  return api.get<{ data: WorksheetPhoto[] }>(`/worksheets/${worksheetId}/photos`);
}

export async function uploadWorksheetPhoto(
  worksheetId: string,
  file: File,
  caption?: string,
  photoType?: WorksheetPhotoType
): Promise<{ data: WorksheetPhoto }> {
  const formData = new FormData();
  formData.append('file', file);
  if (caption) formData.append('caption', caption);
  if (photoType) formData.append('photoType', photoType);

  return api.upload<{ data: WorksheetPhoto }>(`/worksheets/${worksheetId}/photos`, formData);
}

export async function deleteWorksheetPhoto(
  worksheetId: string,
  photoId: string
): Promise<{ data: { success: boolean } }> {
  return api.delete<{ data: { success: boolean } }>(`/worksheets/${worksheetId}/photos/${photoId}`);
}

// ============================================
// SRV-FG-3: WARRANTY CLAIM API
// ============================================

export interface WarrantyClaimInfo {
  id: string;
  claimNumber: string;
  status: string;
  manufacturer: string;
  totalClaimValue?: number;
  settledAmount?: number;
  submittedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  notes?: string;
}

export async function getWorksheetWarrantyClaim(
  worksheetId: string
): Promise<{ data: WarrantyClaimInfo | null }> {
  return api.get<{ data: WarrantyClaimInfo | null }>(`/worksheets/${worksheetId}/warranty-claim`);
}

export async function submitWarrantyClaim(
  worksheetId: string,
  data: { manufacturer?: string; notes?: string }
): Promise<{ data: WarrantyClaimInfo }> {
  return api.post<{ data: WarrantyClaimInfo }>(
    `/worksheets/${worksheetId}/warranty-claim/submit`,
    data
  );
}

// ============================================
// SRV-FG-3: NOTIFICATION API
// ============================================

export async function sendWorksheetNotification(
  worksheetId: string,
  eventType: 'RECEIVED' | 'COMPLETED' | 'SUSPENDED' | 'QUOTE_SENT',
  language?: 'hu' | 'en' | 'de' | 'uk'
): Promise<{ data: { sent: boolean; reason?: string } }> {
  return api.post<{ data: { sent: boolean; reason?: string } }>(
    `/worksheets/${worksheetId}/notify`,
    { eventType, language: language ?? 'hu' }
  );
}

// ============================================
// SRV-FG-5-9: TECHNICIAN ASSIGNMENT API
// ============================================

export interface TechnicianAssignment {
  userId: string;
  name: string;
  role: 'PRIMARY' | 'CO_TECH';
}

export interface TechnicianAssignmentResponse {
  data: {
    technicians: TechnicianAssignment[];
    maxCoTechnicians: number;
    canAddMore: boolean;
  };
}

export async function getWorksheetTechnicians(
  worksheetId: string
): Promise<TechnicianAssignmentResponse> {
  return api.get<TechnicianAssignmentResponse>(`/worksheets/${worksheetId}/technicians`);
}

export async function assignWorksheetTechnician(
  worksheetId: string,
  data: { userId: string; name: string; role: 'PRIMARY' | 'CO_TECH' }
): Promise<TechnicianAssignmentResponse> {
  return api.post<TechnicianAssignmentResponse>(
    `/worksheets/${worksheetId}/assign-technician`,
    data
  );
}

export async function removeWorksheetTechnician(
  worksheetId: string,
  userId: string
): Promise<TechnicianAssignmentResponse> {
  return api.delete<TechnicianAssignmentResponse>(
    `/worksheets/${worksheetId}/technicians/${userId}`
  );
}

// ============================================
// SRV-FG-5-1: DISASSEMBLY PROTOCOL API
// ============================================

export interface DisassemblyPart {
  productId: string;
  quantity: number;
  condition: 'NEW' | 'USED' | 'DAMAGED';
}

export interface DisassemblyRecord {
  id: string;
  worksheetId: string;
  worksheetNumber: string;
  equipmentName: string;
  parts: DisassemblyPart[];
  performedBy: string;
  performedAt: string;
  stockMovementIds: string[];
}

export async function executeDisassembly(
  worksheetId: string,
  parts: DisassemblyPart[]
): Promise<{ data: DisassemblyRecord }> {
  return api.post<{ data: DisassemblyRecord }>('/service/disassembly', { worksheetId, parts });
}

export async function getDisassemblyProtocols(): Promise<{ data: DisassemblyRecord[] }> {
  return api.get<{ data: DisassemblyRecord[] }>('/service/disassembly-protocols');
}

// ============================================
// SRV-FG-5-2: EXPRESS SURCHARGE API
// ============================================

export async function addExpressSurcharge(
  worksheetId: string,
  amount?: number
): Promise<{ data: { success: boolean; itemId?: string; amount: number } }> {
  return api.post<{ data: { success: boolean; itemId?: string; amount: number } }>(
    `/service/worksheets/${worksheetId}/express-surcharge`,
    { amount }
  );
}

// ============================================
// SRV-FG-5-3: EXTERNAL SERVICE FORWARDING API
// ============================================

export interface ExternalForwardRecord {
  worksheetId: string;
  externalPartnerId: string;
  externalPartnerName?: string;
  reason: string;
  forwardedAt: string;
  forwardedBy: string;
  estimatedReturnDate: string;
  actualReturnDate?: string;
  actualCost?: number;
  returnNotes?: string;
  status: 'FORWARDED' | 'RETURNED';
}

export async function forwardToExternal(
  worksheetId: string,
  data: { externalPartnerId: string; reason: string; estimatedReturnDate: string }
): Promise<{ data: ExternalForwardRecord }> {
  return api.post<{ data: ExternalForwardRecord }>(
    `/service/worksheets/${worksheetId}/forward-external`,
    data
  );
}

export async function returnFromExternal(
  worksheetId: string,
  data: { actualCost: number; notes: string }
): Promise<{ data: ExternalForwardRecord }> {
  return api.post<{ data: ExternalForwardRecord }>(
    `/service/worksheets/${worksheetId}/return-from-external`,
    data
  );
}

export async function getExternalForward(
  worksheetId: string
): Promise<{ data: ExternalForwardRecord | null }> {
  return api.get<{ data: ExternalForwardRecord | null }>(
    `/service/worksheets/${worksheetId}/external-forward`
  );
}

// ============================================
// SRV-FG-5-4: PUBLIC SERVICE REQUEST API
// ============================================

export interface ServiceRequestData {
  id: string;
  customerName: string;
  phone: string;
  email?: string;
  equipmentDescription: string;
  problemDescription: string;
  preferredDate?: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  createdAt: string;
}

export async function submitPublicServiceRequest(data: {
  customerName: string;
  phone: string;
  email?: string;
  equipmentDescription: string;
  problemDescription: string;
  preferredDate?: string;
}): Promise<{ data: { id: string; message: string } }> {
  return api.post<{ data: { id: string; message: string } }>('/public/service-request', data);
}

export async function getPendingServiceRequests(
  status?: string
): Promise<{ data: ServiceRequestData[] }> {
  const params = status ? `?status=${status}` : '';
  return api.get<{ data: ServiceRequestData[] }>(`/service/pending-requests${params}`);
}

export async function acceptServiceRequest(
  requestId: string
): Promise<{ data: { requestId: string; worksheetId?: string; status: string } }> {
  return api.post<{ data: { requestId: string; worksheetId?: string; status: string } }>(
    `/service/pending-requests/${requestId}/accept`,
    {}
  );
}

export async function rejectServiceRequest(
  requestId: string
): Promise<{ data: { requestId: string; status: string } }> {
  return api.post<{ data: { requestId: string; status: string } }>(
    `/service/pending-requests/${requestId}/reject`,
    {}
  );
}

// ============================================
// SRV-F1-3: CATEGORY HOURLY RATE CONFIG API
// ============================================

export interface HourlyRateConfig {
  id: string;
  machineCategoryId: string;
  categoryCode: string;
  categoryName: string;
  hourlyRateNet: number;
  validFrom: string;
  validUntil: string | null;
}

export interface MachineCategory {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
}

export async function getMachineCategories(): Promise<{ data: MachineCategory[] }> {
  return api.get<{ data: MachineCategory[] }>('/machine-categories');
}

export async function getHourlyRateConfigs(): Promise<{ data: HourlyRateConfig[] }> {
  return api.get<{ data: HourlyRateConfig[] }>('/machine-categories/hourly-rates');
}

export async function upsertHourlyRateConfig(data: {
  machineCategoryId: string;
  hourlyRateNet: number;
  validFrom?: string;
  validUntil?: string | null;
}): Promise<{ data: HourlyRateConfig }> {
  return api.post<{ data: HourlyRateConfig }>('/machine-categories/hourly-rates', data);
}

export async function deleteHourlyRateConfig(id: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/machine-categories/hourly-rates/${id}`);
}

// ============================================
// SRV-F1-5: SERVICE PROCUREMENT REQUEST API
// ============================================

export interface ProcurementRequest {
  id: string;
  worksheetId: string;
  worksheetNumber: string;
  productId: string | null;
  description: string;
  quantity: number;
  status: 'PENDING' | 'ORDERED' | 'RECEIVED' | 'CANCELLED';
  note: string | null;
  requestedBy: string;
  fulfilledAt: string | null;
  createdAt: string;
}

export async function getProcurementRequests(
  status?: string
): Promise<{ data: ProcurementRequest[] }> {
  const endpoint = status
    ? `/service/procurement-requests?status=${status}`
    : '/service/procurement-requests';
  return api.get<{ data: ProcurementRequest[] }>(endpoint);
}

export async function getProcurementRequestsByWorksheet(
  worksheetId: string
): Promise<{ data: ProcurementRequest[] }> {
  return api.get<{ data: ProcurementRequest[] }>(
    `/service/procurement-requests/by-worksheet/${worksheetId}`
  );
}

export async function createProcurementRequest(data: {
  worksheetId: string;
  productId?: string | null;
  description: string;
  quantity?: number;
  note?: string | null;
}): Promise<{ data: ProcurementRequest }> {
  return api.post<{ data: ProcurementRequest }>('/service/procurement-requests', data);
}

export async function updateProcurementRequestStatus(
  id: string,
  status: 'ORDERED' | 'RECEIVED' | 'CANCELLED',
  note?: string | null
): Promise<{ data: ProcurementRequest }> {
  return api.patch<{ data: ProcurementRequest }>(`/service/procurement-requests/${id}/status`, {
    status,
    note,
  });
}
